import React, { useState, useCallback, useEffect } from 'react';
import {
  Upload,
  Database,
  X,
  ShieldCheck,
  ChevronRight,
  ChevronLeft,
  Search,
  Check,
  ArrowLeft,
  Table,
  AlertCircle,
  ExternalLink,
  Cloud,
  Trash2,
  RefreshCw,
  Library,
  Server,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { bigqueryApi } from '../../api/bigqueryApi';
import { datasetApi } from '../../api/datasetApi';

const ConnectModal = ({ isOpen, mode = 'table', onClose, onConnect, onBillingRequired, initialStep, currentSources = [] }) => {
  const [step, setStep] = useState('select'); // 'select' | 'upload' | 'samples' | 'bigquery-auth' | 'bigquery-dataset' | 'bigquery-table' | 'bigquery-confirm' | 'existing'
  const [searchQuery, setSearchQuery] = useState('');
  const [selection, setSelection] = useState({
    project: null,
    dataset: null,
    tables: []
  });
  const [isUploading, setIsUploading] = useState(false);
  const [bqStatus, setBqStatus] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [tables, setTables] = useState([]);
  const [allUserDatasets, setAllUserDatasets] = useState([]);
  const [sampleDatasets, setSampleDatasets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isGeneratingMetadata, setIsGeneratingMetadata] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentTablePage, setCurrentTablePage] = useState(1);
  const [itemsPerPage] = useState(10);

  const checkBQStatus = useCallback(async () => {
    if (!isOpen) return;

    try {
      const status = await bigqueryApi.getStatus();
      setBqStatus(status);
      // Removed dependency on 'step' here to avoid reload loops
      // The logic to load projects if connected is moved elsewhere or handled differently
    } catch (err) {
      if (err.code !== 'ECONNABORTED') {
        console.error('Failed to check BQ status:', err);
      }
    }
  }, [isOpen]); // Added isOpen to deps


  const loadDatasets = useCallback(async (projectId) => {
    setIsLoading(true);
    try {
      const data = await bigqueryApi.getDatasets(projectId);
      setDatasets(data);
      setStep('bigquery-dataset');
    } catch (err) {
      console.error('Failed to load datasets:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadTables = useCallback(async (projectId, datasetId) => {
    setIsLoading(true);
    try {
      const data = await bigqueryApi.getTables(projectId, datasetId);
      setTables(data);
      setStep('bigquery-table');
    } catch (err) {
      console.error('Failed to load tables:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadAllUserDatasets = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      const data = await datasetApi.getDatasets();
      setAllUserDatasets(data);
    } catch (err) {
      console.error('Failed to load user datasets:', err);
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  const loadSampleDatasets = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await datasetApi.getSamples();
      setSampleDatasets(data);
    } catch (err) {
      console.error('Failed to load sample datasets:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load sample datasets');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleBQLanding = useCallback(async () => {
    if (bqStatus?.connected) {
      if (!bqStatus.billing_project_id) {
        // Connected but no billing project - trigger the billing modal first
        if (onBillingRequired) {
          onBillingRequired();
        } else {
          setStep('bigquery-auth');
        }
      } else {
        // Connected and has billing project - proceed to dataset selection
        setSelection(prev => ({
          ...prev,
          project: { project_id: bqStatus.billing_project_id, friendly_name: bqStatus.billing_project_id }
        }));
        loadDatasets(bqStatus.billing_project_id);
      }
    } else {
      setStep('bigquery-auth');
    }
  }, [bqStatus, loadDatasets, onBillingRequired]);

  React.useEffect(() => {
    if (isOpen) {
      checkBQStatus();
      if (mode === 'workspace' || step === 'existing') {
        loadAllUserDatasets();
      }
    }
  }, [isOpen, checkBQStatus, mode, step, loadAllUserDatasets]);

  // Handle auto-project load if we just authorized or resumed
  useEffect(() => {
    if (isOpen && bqStatus?.connected && (step === 'bigquery-auth' || (step === 'select' && initialStep === 'bigquery-dataset'))) {
      handleBQLanding();
    }
  }, [isOpen, bqStatus, step, initialStep, handleBQLanding]);



  // Polling for library updates when datasets are loading/generating
  useEffect(() => {
    if (!isOpen) return;

    const hasPending = allUserDatasets.some(d => d.status === 'loading' || d.status === 'generating');
    if (!hasPending) return;

    const interval = setInterval(() => {
      loadAllUserDatasets(true); // Silent update
    }, 3000);

    return () => clearInterval(interval);
  }, [isOpen, allUserDatasets, loadAllUserDatasets]);

  const handleBack = () => {
    if (step === 'upload' || step === 'samples' || step === 'bigquery-auth' || step === 'existing') setStep('select');
    else if (step === 'bigquery-dataset') setStep('select');
    else if (step === 'bigquery-table') setStep('bigquery-dataset');
    else if (step === 'bigquery-confirm') setStep('bigquery-table');
    setError(null);
  };

  const handleBQAuth = async () => {
    try {
      const { url } = await bigqueryApi.getLoginUrl();
      window.location.href = url;
    } catch (err) {
      console.error('Failed to get login URL:', err);
    }
  };

  const handleDatasetSelect = (dataset) => {
    setSelection(prev => ({ ...prev, dataset }));
    loadTables(selection.project.project_id, dataset.dataset_id);
  };

  const fileInputRef = React.useRef(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // Frontend Validation
      const MAX_SIZE = 32 * 1024 * 1024; // 32MB
      if (file.size > MAX_SIZE) {
        throw new Error(`File is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum size is 32MB.`);
      }

      if (!file.name.toLowerCase().endsWith('.parquet')) {
        throw new Error('Invalid file type. Only .parquet files are supported.');
      }

      const result = await datasetApi.uploadDataset(file);

      onConnect('upload', result);
      onClose();
      resetModal();
    } catch (err) {
      // Don't log expected validation errors to the console
      const isValidationError =
        err.message?.includes('File is too large') ||
        err.message?.includes('Invalid file type') ||
        err.response?.status === 413 ||
        err.response?.status === 400;

      if (!isValidationError) {
        console.error('Upload failed:', err);
      }

      const errorMessage = err.response?.data?.detail || err.message || 'Upload failed. Please try again.';
      setError(errorMessage);
    } finally {
      setIsUploading(false);
      setIsGeneratingMetadata(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the drop zone itself
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isUploading || isGeneratingMetadata) return;

    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    // Validate size
    const MAX_SIZE = 32 * 1024 * 1024; // 32MB
    if (file.size > MAX_SIZE) {
      setError(`File is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum size is 32MB.`);
      return;
    }

    // Validate type
    if (!file.name.toLowerCase().endsWith('.parquet')) {
      setError('Invalid file type. Only .parquet files are supported.');
      return;
    }

    setError(null);

    setIsUploading(true);
    try {
      const result = await datasetApi.uploadDataset(file);

      onConnect('upload', result);
      onClose();
      resetModal();
    } catch (err) {
      const isValidationError = err.response?.status === 413 || err.response?.status === 400;
      if (!isValidationError) {
        console.error('Upload failed:', err);
      }
      const errorMessage = err.response?.data?.detail || err.message || 'Upload failed. Please try again.';
      setError(errorMessage);
    } finally {
      setIsUploading(false);
      setIsGeneratingMetadata(false);
    }
  };

  const toggleTableSelection = (table) => {
    setSelection(prev => {
      const exists = prev.tables.find(t => t.id === table.id);
      if (exists) {
        return { ...prev, tables: prev.tables.filter(t => t.id !== table.id) };
      }
      return {
        ...prev,
        tables: [...prev.tables, { ...table, contextProject: selection.project?.project_id, contextDataset: selection.dataset?.dataset_id }]
      };
    });
  };

  const handleFinishBQ = async () => {
    setIsImporting(true);
    try {
      // Transform selected tables to the format workspace expects
      const formattedData = {
        project: selection.project,
        dataset: selection.dataset,
        tables: selection.tables.map(t => ({
          id: t.full_id || t.id,
          name: t.table_id || t.name,
          size: 'N/A', // BQ API could provide this but let's keep it simple
          rows: 'N/A'
        }))
      };
      await onConnect('bigquery', formattedData);
      onClose();
      resetModal();
    } catch (err) {
      console.error('Import failed:', err);
    } finally {
      setIsImporting(false);
    }
  };

  const resetModal = useCallback(() => {
    setStep('select');
    setSearchQuery('');
    setSelection({ project: null, dataset: null, tables: [] });
    setDatasets([]);
    setTables([]);
    setError(null);
    setCurrentPage(1);
    setCurrentTablePage(1);
  }, []);

  useEffect(() => {
    if (isOpen) {
      resetModal();
    }
  }, [isOpen, resetModal]);

  // Reset pagination when search query changes
  useEffect(() => {
    setCurrentPage(1);
    setCurrentTablePage(1);
  }, [searchQuery]);

  // Reset pagination when datasets are loaded
  useEffect(() => {
    setCurrentPage(1);
  }, [datasets.length]);

  // Reset table pagination when tables are loaded
  useEffect(() => {
    setCurrentTablePage(1);
  }, [tables.length]);

  const filteredDatasets = datasets.filter(d =>
    d.dataset_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate pagination for datasets
  const totalPages = Math.ceil(filteredDatasets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDatasets = filteredDatasets.slice(startIndex, endIndex);

  const filteredLibrary = allUserDatasets
    .filter(d => {
      // Enforce single source type per workspace: BigQuery OR samples OR uploads
      const hasBigQuery = currentSources.some(s => s.type === 'bigquery');
      const hasSamples = currentSources.some(s => s.type === 'sample');
      const hasUploads = currentSources.some(s => s.type === 'upload');

      // If workspace has BigQuery, only show BigQuery datasets
      if (hasBigQuery) return d.type === 'bigquery';

      // If workspace has samples, only show samples
      if (hasSamples) return d.type === 'sample';

      // If workspace has uploads, only show uploads
      if (hasUploads) return d.type === 'upload';

      // Empty workspace: show all
      return true;
    })
    .filter(d => (d.name || d.id || "").toLowerCase().includes(searchQuery.toLowerCase()));

  const filteredTables = tables.filter(t =>
    (t.table_id || t.name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate pagination for tables
  const totalTablePages = Math.ceil(filteredTables.length / itemsPerPage);
  const tableStartIndex = (currentTablePage - 1) * itemsPerPage;
  const tableEndIndex = tableStartIndex + itemsPerPage;
  const paginatedTables = filteredTables.slice(tableStartIndex, tableEndIndex);

  if (!isOpen) return null;

  const options = mode === 'connection' ? [
    {
      id: 'bigquery',
      title: 'Google BigQuery',
      desc: 'Connect your Enterprise Data Warehouse',
      icon: Database,
      color: 'text-blue-600',
      bg: 'bg-blue-50 dark:bg-blue-900/20'
    },
    {
      id: 'snowflake',
      title: 'Snowflake',
      desc: 'Cloud data platform',
      icon: Cloud,
      color: 'text-gray-400',
      bg: 'bg-gray-50 dark:bg-gray-900/20',
      disabled: true
    },
    {
      id: 'redshift',
      title: 'Amazon Redshift',
      desc: 'AWS data warehouse',
      icon: Database,
      color: 'text-gray-400',
      bg: 'bg-gray-50 dark:bg-gray-900/20',
      disabled: true
    },
    {
      id: 'databricks',
      title: 'Databricks',
      desc: 'Unified analytics platform',
      icon: Server,
      color: 'text-gray-400',
      bg: 'bg-gray-50 dark:bg-gray-900/20',
      disabled: true
    },
    {
      id: 'postgresql',
      title: 'PostgreSQL',
      desc: 'Open source relational database',
      icon: Database,
      color: 'text-gray-400',
      bg: 'bg-gray-50 dark:bg-gray-900/20',
      disabled: true
    }
  ] : (() => {
    // For table mode (DataSourcesPage), only BigQuery is supported
    if (mode === 'table') {
      return [
        {
          id: 'bigquery',
          title: 'Google BigQuery',
          desc: 'Import from cloud data warehouse',
          icon: Database,
          color: 'text-blue-600',
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          disabled: false
        },
        {
          id: 'upload',
          title: 'Manteo Warehouse',
          desc: 'Subscription does not support this feature',
          icon: Upload,
          color: 'text-gray-400',
          bg: 'bg-gray-50 dark:bg-gray-900/20',
          disabled: true
        },
        {
          id: 'local-file',
          title: 'Add local file',
          desc: 'parquet, CSV, gpkg, shp',
          icon: Upload,
          color: 'text-gray-400',
          bg: 'bg-gray-50 dark:bg-gray-900/20',
          disabled: true
        }
      ];
    }

    // For workspace mode, enforce single source type: BigQuery OR samples OR uploads (not mixed)
    const hasBigQuery = currentSources.some(s => s.type === 'bigquery');
    const hasSamples = currentSources.some(s => s.type === 'sample');
    const hasUploads = currentSources.some(s => s.type === 'upload' || s.type === 'parquet' || s.type === 'csv');

    // Determine which type is locked in (if any)
    const lockedType = hasBigQuery ? 'bigquery' : hasSamples ? 'sample' : hasUploads ? 'upload' : null;

    return [
      {
        id: 'bigquery',
        title: 'Google BigQuery',
        desc: lockedType && lockedType !== 'bigquery'
          ? `Disabled: Workspace uses ${lockedType === 'sample' ? 'samples' : 'uploads'}`
          : 'Import from cloud data warehouse',
        icon: Database,
        color: 'text-blue-600',
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        disabled: lockedType === 'sample' || lockedType === 'upload'
      },
      {
        id: 'existing',
        title: 'Manteo Library',
        desc: 'Attach an existing dataset',
        icon: Library,
        color: 'text-gray-400',
        bg: 'bg-gray-50 dark:bg-gray-900/20',
        disabled: true
      },
      {
        id: 'upload',
        title: 'Manteo Warehouse',
        desc: 'Upload files or select samples',
        icon: Upload,
        color: 'text-gray-400',
        bg: 'bg-gray-50 dark:bg-gray-900/20',
        disabled: true
      }
    ];
  })();

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-950 w-full max-w-lg rounded-[40px] shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]">

        {/* Header */}
        <div className="p-8 border-b border-gray-50 dark:border-gray-900/50 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            {step !== 'select' && (
              <button onClick={handleBack} className="p-2 -ml-2 text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-xl transition-colors">
                <ArrowLeft size={20} />
              </button>
            )}
            <div>
              <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tighter leading-none">
                {step === 'select' && (
                  mode === 'connection'
                    ? (bqStatus?.connected ? "Manage Connection" : "Add Connection")
                    : "Add Dataset"
                )}
                {step === 'upload' && "Manteo Warehouse"}
                {step === 'samples' && "Sample Datasets"}
                {step === 'existing' && "Select from Library"}
                {step === 'bigquery-auth' && "Connect BigQuery"}
                {step === 'bigquery-dataset' && "Select Dataset"}
                {step === 'bigquery-table' && "Select Tables"}
                {step === 'bigquery-confirm' && "Review Import"}
              </h3>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                {step === 'select' && (mode === 'connection' ? "Configure your data source provider" : "Choose how you want to add data")}
                {step === 'upload' && "Upload your own files or select samples"}
                {step === 'samples' && "Choose from available sample datasets"}
                {step === 'existing' && "Select a dataset already in your Manteo Warehouse"}
                {step === 'bigquery-auth' && "Authenticate via Google SSO"}
                {step === 'bigquery-dataset' && "Choose a BigQuery dataset"}
                {step === 'bigquery-table' && (selection.project ? `${selection.project.friendly_name} / ${selection.dataset?.dataset_id}` : "Select tables to import")}
                {step === 'bigquery-confirm' && "Verify selected datasets before adding"}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mx-8 mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/50 rounded-2xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2 shrink-0">
            <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-[10px] font-black text-red-600 dark:text-red-400 uppercase tracking-widest">Error</p>
              <p className="text-xs font-bold text-red-500 dark:text-red-300 mt-1">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="p-1 hover:bg-red-100 dark:hover:bg-red-800 rounded-lg transition-colors">
              <X size={14} className="text-red-400" />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar min-h-[300px]">
          {step === 'select' && (
            <div className="space-y-2">
              {options.map((opt) => (
                <button
                  key={opt.id}
                  disabled={opt.disabled}
                  onClick={() => {
                    if (opt.id === 'bigquery') handleBQLanding();
                    else setStep(opt.id);
                  }}
                  className={cn(
                    "w-full flex items-center gap-4 p-5 rounded-3xl transition-all border border-transparent text-left",
                    opt.disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50 dark:hover:bg-gray-900 hover:border-gray-100 dark:hover:border-gray-800 group"
                  )}
                >
                  <div className={cn("p-4 rounded-2xl transition-transform shadow-sm", !opt.disabled && "group-hover:scale-110", opt.bg, opt.color)}>
                    <opt.icon size={28} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tight">{opt.title}</span>
                      {opt.id === 'bigquery' && bqStatus?.reauth_required && (
                        <div className="flex items-center gap-1 px-2 py-0.5 bg-red-100/50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg">
                          <AlertCircle size={10} />
                          <span className="text-[8px] font-black uppercase tracking-widest">Re-auth Required</span>
                        </div>
                      )}
                      {opt.id === 'bigquery' && bqStatus?.connected && !bqStatus.reauth_required && !bqStatus.billing_project_id && (
                        <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-100/50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg">
                          <AlertCircle size={10} />
                          <span className="text-[8px] font-black uppercase tracking-widest">Setup Required</span>
                        </div>
                      )}
                    </div>
                    <p className="text-[10px] text-gray-500 font-medium mt-0.5">{opt.desc}</p>
                  </div>
                  {!opt.disabled && <ChevronRight size={20} className="text-gray-300 group-hover:text-emerald-500 transition-colors" />}
                </button>
              ))}
            </div>
          )}

          {step === 'upload' && (
            <div className="space-y-3">
              <button
                onClick={() => {
                  loadSampleDatasets();
                  setStep('samples');
                }}
                className="w-full flex items-center gap-4 p-5 rounded-3xl transition-all border border-transparent hover:bg-gray-50 dark:hover:bg-gray-900 hover:border-gray-100 dark:hover:border-gray-800 group"
              >
                <div className="p-4 rounded-2xl transition-transform shadow-sm group-hover:scale-110 bg-amber-50 dark:bg-amber-900/20 text-amber-600">
                  <Library size={28} />
                </div>
                <div className="flex-1 text-left">
                  <span className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tight">Select Sample Dataset</span>
                  <p className="text-[10px] text-gray-500 font-medium mt-0.5">Choose from pre-loaded examples</p>
                </div>
                <ChevronRight size={20} className="text-gray-300 group-hover:text-amber-500 transition-colors" />
              </button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200 dark:border-gray-800"></div>
                </div>
                <div className="relative flex justify-center text-[8px] uppercase tracking-widest">
                  <span className="px-2 bg-white dark:bg-gray-950 text-gray-400 font-black">Or</span>
                </div>
              </div>

              <div className="space-y-4">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="hidden"
                  accept=".parquet"
                />
                <div
                  className={cn(
                    "w-full border-2 border-dashed rounded-[40px] p-12 flex flex-col items-center justify-center group transition-all",
                    (isUploading || isGeneratingMetadata) ? "opacity-70 cursor-wait border-gray-200 dark:border-gray-800" :
                      isDragging ? "border-emerald-500 bg-emerald-50/10 dark:bg-emerald-900/10 scale-[1.02]" :
                        "border-gray-200 dark:border-gray-800 hover:border-emerald-500/50 hover:bg-emerald-50/5 dark:hover:bg-emerald-900/5 cursor-pointer"
                  )}
                  onClick={() => !(isUploading || isGeneratingMetadata) && handleUploadClick()}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className={cn(
                    "p-6 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 rounded-[32px] mb-6 transition-transform",
                    isDragging ? "scale-110" : "group-hover:scale-110"
                  )}>
                    {(isUploading || isGeneratingMetadata) ? (
                      <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Upload size={32} />
                    )}
                  </div>
                  <h4 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-widest mb-2">
                    {isUploading ? "Uploading File..." : isGeneratingMetadata ? "Enriching Metadata..." : isDragging ? "Drop to Upload" : "Upload Your Own File"}
                  </h4>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest text-center max-w-[200px]">
                    {isUploading ? "Sending to Manteo Warehouse" : isGeneratingMetadata ? "Analyzing your dataset" : "Drop files or click to browse"}
                  </p>
                </div>
                <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-2xl w-full">
                  <AlertCircle size={16} className="text-amber-500 shrink-0" />
                  <p className="text-[9px] font-bold text-gray-500 uppercase tracking-[0.2em]">
                    Supported format: .parquet (Max: 32MB)
                  </p>
                </div>
              </div>
            </div>
          )}

          {step === 'samples' && (
            <div className="space-y-3">
              <div className="relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-amber-500 transition-colors" size={18} />
                <input
                  type="text"
                  placeholder="Search samples..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-900 border border-transparent focus:border-amber-500/50 rounded-2xl text-xs font-bold outline-none transition-all"
                />
              </div>
              <div className="space-y-1 pt-2">
                {isLoading ? (
                  <div className="py-12 flex flex-col items-center justify-center gap-4">
                    <div className="w-8 h-8 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Loading Samples...</span>
                  </div>
                ) : sampleDatasets.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 ? (
                  <div className="py-12 text-center text-gray-400 text-[10px] font-bold uppercase tracking-widest">No samples found</div>
                ) : sampleDatasets.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase())).map((sample) => (
                  <button
                    key={sample.id}
                    onClick={async () => {
                      setIsImporting(true);
                      try {
                        // Add sample to user's datasets with background metadata generation
                        await datasetApi.addSample(sample.id);
                        onConnect('sample', sample);
                        onClose();
                        resetModal();
                      } catch (err) {
                        console.error('Failed to add sample:', err);
                        alert('Failed to add sample dataset. Please try again.');
                      } finally {
                        setIsImporting(false);
                      }
                    }}
                    disabled={isImporting}
                    className={cn(
                      "w-full flex items-center justify-between p-4 rounded-2xl group transition-all",
                      isImporting ? "opacity-60 cursor-not-allowed" : "hover:bg-amber-50 dark:hover:bg-amber-900/20"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white dark:bg-gray-800 rounded-xl text-amber-600 shadow-sm">
                        <Database size={16} />
                      </div>
                      <div className="text-left">
                        <div className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-tight">{sample.name}</div>
                        <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Sample Dataset</div>
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-gray-300 group-hover:text-amber-500 transition-colors" />
                  </button>
                ))}
              </div>
            </div>
          )}


          {step === 'existing' && (
            <div className="space-y-3">
              <div className="relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-500 transition-colors" size={18} />
                <input
                  type="text"
                  placeholder="Search your library..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-900 border border-transparent focus:border-emerald-500/50 rounded-2xl text-xs font-bold outline-none transition-all"
                />
              </div>
              <div className="space-y-1 pt-2">
                {isLoading ? (
                  <div className="py-12 flex flex-col items-center justify-center gap-4">
                    <div className="w-8 h-8 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Loading Library...</span>
                  </div>
                ) : filteredLibrary.length === 0 ? (
                  <div className="py-12 text-center text-gray-400 text-[10px] font-bold uppercase tracking-widest">No datasets found</div>
                ) : filteredLibrary.map((dataset) => (
                  <button
                    key={dataset.id}
                    onClick={() => {
                      if (dataset.status && dataset.status !== 'ready') return;
                      onConnect('library', dataset);
                      onClose();
                      resetModal();
                    }}
                    disabled={dataset.status && dataset.status !== 'ready'}
                    className={cn(
                      "w-full flex items-center justify-between p-4 rounded-2xl group transition-all",
                      dataset.status && dataset.status !== 'ready'
                        ? "opacity-60 cursor-not-allowed"
                        : "hover:bg-amber-50 dark:hover:bg-amber-900/20"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white dark:bg-gray-800 rounded-xl text-amber-600 shadow-sm">
                        <Database size={16} />
                      </div>
                      <div className="text-left">
                        <div className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-tight">{dataset.name || dataset.id}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{dataset.type}</div>
                          {dataset.status && dataset.status !== 'ready' && (
                            <span className={cn(
                              "text-[7px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full border shrink-0",
                              dataset.status === 'failed' ? "bg-red-50 text-red-500 border-red-100" :
                                "bg-amber-50 text-amber-500 border-amber-100 animate-pulse"
                            )}>
                              {dataset.status === 'loading' ? 'Loading' :
                                dataset.status === 'generating' ? 'Generating' :
                                  dataset.status}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {dataset.status && dataset.status !== 'ready' ? (
                      <AlertCircle size={16} className="text-amber-500/50" />
                    ) : (
                      <ChevronRight size={16} className="text-gray-300 group-hover:text-amber-500 transition-colors" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 'bigquery-auth' && (
            <div className="h-full flex flex-col items-center justify-center p-8 space-y-8">
              <div className="relative">
                <div className="absolute inset-0 bg-blue-500/20 blur-3xl rounded-full" />
                <div className="relative p-8 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded-[40px] shadow-sm">
                  <ShieldCheck size={48} />
                </div>
              </div>
              <div className="text-center">
                <h4 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tight mb-2">Google Cloud Authentication</h4>
                <p className="text-[11px] text-gray-500 font-medium max-w-[280px] mx-auto leading-relaxed">
                  We need your permission to access your BigQuery projects and datasets.
                </p>
              </div>
              <button
                onClick={handleBQAuth}
                className="w-full flex items-center justify-center gap-3 bg-blue-600 text-white p-5 rounded-[24px] font-black uppercase tracking-widest text-[11px] hover:bg-blue-700 transition-all shadow-xl shadow-blue-600/20 active:scale-[0.98]"
              >
                Connect Google Account
                <ExternalLink size={16} />
              </button>
              <p className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.2em]">Secure OAuth2 Verification</p>
            </div>
          )}


          {step === 'bigquery-dataset' && (
            <div className="space-y-3">
              <div className="relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-500 transition-colors" size={18} />
                <input
                  type="text"
                  placeholder="Filter datasets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-900 border border-transparent focus:border-emerald-500/50 rounded-2xl text-xs font-bold outline-none transition-all"
                />
              </div>
              {!isLoading && filteredDatasets.length > 0 && totalPages > 1 && (
                <div className="flex items-center justify-between px-2 py-2">
                  <div className="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                    Showing {startIndex + 1}-{Math.min(endIndex, filteredDatasets.length)} of {filteredDatasets.length} datasets
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className={cn(
                        "p-1.5 rounded-lg transition-all",
                        currentPage === 1
                          ? "opacity-50 cursor-not-allowed text-gray-400"
                          : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-blue-600 dark:hover:text-blue-400"
                      )}
                    >
                      <ChevronLeft size={14} />
                    </button>
                    <span className="text-[9px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest px-2">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage >= totalPages}
                      className={cn(
                        "p-1.5 rounded-lg transition-all",
                        currentPage >= totalPages
                          ? "opacity-50 cursor-not-allowed text-gray-400"
                          : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-blue-600 dark:hover:text-blue-400"
                      )}
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
              <div className="space-y-1">
                {isLoading ? (
                  <div className="py-12 flex flex-col items-center justify-center gap-4">
                    <div className="w-8 h-8 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Loading datasets...</span>
                  </div>
                ) : filteredDatasets.length === 0 ? (
                  <div className="py-12 text-center text-gray-400 text-[10px] font-bold uppercase tracking-widest">No datasets found</div>
                ) : (
                  <>
                    {paginatedDatasets.map((dataset) => (
                      <button
                        key={dataset.dataset_id}
                        onClick={() => handleDatasetSelect(dataset)}
                        className="w-full flex items-center justify-between p-4 rounded-2xl hover:bg-blue-50 dark:hover:bg-blue-900/20 group transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-white dark:bg-gray-800 rounded-xl text-blue-600 shadow-sm">
                            <Database size={16} />
                          </div>
                          <div className="text-left font-black text-xs text-gray-900 dark:text-white uppercase tracking-tight">
                            {dataset.dataset_id}
                          </div>
                        </div>
                        <ChevronRight size={16} className="text-gray-300 group-hover:text-blue-500 transition-colors" />
                      </button>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}

          {step === 'bigquery-table' && (
            <div className="space-y-3">
              <div className="relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-500 transition-colors" size={18} />
                <input
                  type="text"
                  placeholder="Search in this dataset..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-900 border border-transparent focus:border-emerald-500/50 rounded-2xl text-xs font-bold outline-none transition-all"
                />
              </div>
              {!isLoading && filteredTables.length > 0 && totalTablePages > 1 && (
                <div className="flex items-center justify-between px-2 py-2">
                  <div className="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                    Showing {tableStartIndex + 1}-{Math.min(tableEndIndex, filteredTables.length)} of {filteredTables.length} tables
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentTablePage(prev => Math.max(1, prev - 1))}
                      disabled={currentTablePage === 1}
                      className={cn(
                        "p-1.5 rounded-lg transition-all",
                        currentTablePage === 1
                          ? "opacity-50 cursor-not-allowed text-gray-400"
                          : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-blue-600 dark:hover:text-blue-400"
                      )}
                    >
                      <ChevronLeft size={14} />
                    </button>
                    <span className="text-[9px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest px-2">
                      Page {currentTablePage} of {totalTablePages}
                    </span>
                    <button
                      onClick={() => setCurrentTablePage(prev => Math.min(totalTablePages, prev + 1))}
                      disabled={currentTablePage >= totalTablePages}
                      className={cn(
                        "p-1.5 rounded-lg transition-all",
                        currentTablePage >= totalTablePages
                          ? "opacity-50 cursor-not-allowed text-gray-400"
                          : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-blue-600 dark:hover:text-blue-400"
                      )}
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
              <div>
                {isLoading ? (
                  <div className="py-12 flex flex-col items-center justify-center gap-4">
                    <div className="w-8 h-8 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Scanning tables...</span>
                  </div>
                ) : filteredTables.length === 0 ? (
                  <div className="py-12 text-center text-gray-400 text-[10px] font-bold uppercase tracking-widest">No tables found</div>
                ) : (
                  <>
                    <div className="border border-gray-100/50 dark:border-gray-800/50 rounded-2xl overflow-hidden">
                      {paginatedTables.map((table) => (
                        <button
                          key={table.full_id}
                          onClick={() => toggleTableSelection({ id: table.full_id, name: table.table_id, ...table })}
                          className={cn(
                            "w-full flex items-center justify-between py-2.5 px-6 group transition-all border-b border-gray-50/50 dark:border-gray-800/30 last:border-b-0",
                            selection.tables.find(t => t.id === table.full_id)
                              ? "bg-blue-50/20 dark:bg-blue-900/10"
                              : "hover:bg-emerald-50/20 dark:hover:bg-emerald-900/5"
                          )}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Table size={11} className={cn(
                              selection.tables.find(t => t.id === table.full_id) ? "text-blue-600 dark:text-blue-400" : "text-gray-400"
                            )} />
                            <div className="flex-1 min-w-0 text-left">
                              <div className="text-[11px] font-bold text-gray-900 dark:text-white uppercase tracking-wider block truncate max-w-[300px]">
                                {table.table_id}
                              </div>
                              <div className="text-[8px] font-mono text-gray-400 dark:text-gray-500 truncate block max-w-[300px] mt-0.5 opacity-60">
                                {table.type}
                              </div>
                            </div>
                          </div>
                          {selection.tables.find(t => t.id === table.full_id) && (
                            <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center text-white shrink-0">
                              <Check size={12} strokeWidth={3} />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {step === 'bigquery-confirm' && (
            <div className="space-y-2">
              <div className="mb-4 px-2">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Selected Tables</p>
              </div>
              {selection.tables.length === 0 ? (
                <div className="py-12 text-center text-gray-400 text-[10px] font-bold uppercase tracking-widest">
                  No tables selected
                </div>
              ) : (
                selection.tables.map((table) => (
                  <div
                    key={table.id}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-800"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Table size={11} className="text-gray-400" />
                      <div className="flex-1 min-w-0 text-left">
                        <div className="text-[11px] font-bold text-gray-900 dark:text-white uppercase tracking-wider block truncate max-w-[300px]">
                          {table.table_id || table.name}
                        </div>
                        <div className="text-[8px] font-mono text-gray-400 dark:text-gray-500 truncate block max-w-[300px] mt-0.5 opacity-60">
                          {table.contextProject} • {table.contextDataset}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleTableSelection(table)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {selection.tables.length > 0 && (step === 'bigquery-table' || step === 'bigquery-confirm') && (
          <div className="p-8 border-t border-gray-50 dark:border-gray-900/50 bg-gray-50/30 dark:bg-gray-900/30 shrink-0">
            {step === 'bigquery-table' ? (
              <button
                onClick={() => setStep('bigquery-confirm')}
                className="w-full bg-emerald-600 text-white p-5 rounded-[24px] font-black uppercase tracking-widest text-[11px] hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-600/20 active:scale-[0.98] flex items-center justify-center gap-2"
              >
                Add {selection.tables.length} {selection.tables.length === 1 ? 'Table' : 'Tables'}
                <Check size={16} strokeWidth={3} />
              </button>
            ) : (
              <button
                onClick={handleFinishBQ}
                disabled={isImporting}
                className={cn(
                  "w-full bg-emerald-600 text-white p-5 rounded-[24px] font-black uppercase tracking-widest text-[11px] hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-600/20 active:scale-[0.98] flex items-center justify-center gap-2",
                  isImporting && "opacity-70 cursor-wait"
                )}
              >
                {isImporting ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Generating Metadata...
                  </>
                ) : (
                  <>
                    Import Selected Tables
                    <Check size={16} strokeWidth={3} />
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectModal;
